import { serialize } from "object-to-formdata";
import request from "../../utils/request";

import {
  ClockingsDailyRowView, ClockingsPerUserPerDayView, ClockingsReportInput, LeavesReportView, LeavesReportInput, LeavesBalanceReportView, ShiftReportView, LeavesBalanceReportInput, UsersStatusesReportInput, UsersStatusesPaginatedView, ShiftsReportInput, OutOfBoundsReportInput, OutOfBoundsClockView, OvertimeReportInput, OvertimeReportView,
} from "../../models/attendance";

import { Result } from "../../models/general";
import { WorkSessionReportView, WorkSessionsReportInput } from "../../models/projectManagement";


// Try to extract filename from Content-Disposition; return undefined if not present.
function extractFilenameFromHeaders(headers: any): string | undefined {
  const cd: string | undefined = headers?.["content-disposition"] || headers?.["Content-Disposition"];
  if (!cd) return undefined;
  // RFC 5987 (filename*), then fallback to filename="..."
  const utf8Match = cd.match(/filename\*=UTF-8''([^;\n]+)/i);
  if (utf8Match && utf8Match[1]) {
    try { return decodeURIComponent(utf8Match[1]); } catch { /* noop */ }
  }
  const simpleMatch = cd.match(/filename="?([^";\n]+)"?/i);
  if (simpleMatch && simpleMatch[1]) return simpleMatch[1];
  return undefined;
}

function yyyymmdd(d: any): string | undefined {
  try {
    const dt = d instanceof Date ? d : new Date(d);
    if (isNaN(dt.getTime())) return undefined;
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${y}${m}${day}`;
  } catch { return undefined; }
}

// Build a reasonable fallback name like: <prefix>-<from>-<to>-<timestamp>.<ext>
function buildFallbackFilename(prefix: string, format: string, input?: any): string {
  const ext = (format || '').toLowerCase() === 'pdf' ? 'pdf' : 'xlsx';
  const from = input?.dateFrom ?? input?.from ?? input?.fromDate ?? input?.startDate ?? input?.DateFrom ?? input?.StartDate;
  const to = input?.dateTo ?? input?.to ?? input?.toDate ?? input?.endDate ?? input?.DateTo ?? input?.EndDate;
  const fromPart = yyyymmdd(from);
  const toPart = yyyymmdd(to);
  const range = fromPart && toPart ? `${fromPart}-${toPart}` : (fromPart || toPart);
  const now = new Date();
  const ts = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
  return `${prefix}${range ? `-${range}` : ''}-${ts}.${ext}`;
}

function downloadBlob(data: ArrayBuffer, type: string, fileName: string) {
  const url = window.URL.createObjectURL(new Blob([data], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, 0);
}


export async function getClockingsPerUserPerDay(input: ClockingsReportInput) {
  const response = await request.post<Result<ClockingsPerUserPerDayView[][]>>(`reports/Clockings/`,
    serialize(input));
  return response.data;
}

export async function exportClockingsPerUserPerDay(input: ClockingsReportInput, format: string) {
  const response = await request.post<ArrayBuffer>(`reports/Clockings/${format}`,
    serialize(input),
    {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json'
      }
    });
  const suggested = extractFilenameFromHeaders(response.headers);
  const fileName = suggested ?? buildFallbackFilename('clockings', format, input);
  const mime = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  downloadBlob(response.data, mime, fileName);
}

/**
 * The DAY PIVOT of the clockings report — one row per employee per local calendar day.
 *
 * Same `[FromForm]` serialize contract as `getClockingsPerUserPerDay` above and the SAME
 * authorization; only the shape differs. The legacy per-punch call is left in place because mobile
 * still uses it.
 */
export async function getClockingsDaily(input: ClockingsReportInput) {
  const response = await request.post<Result<ClockingsDailyRowView[]>>(`reports/Clockings/Daily`,
    serialize(input));
  return response.data;
}

export async function getLeavesPerUser(input: LeavesReportInput) {
  const response = await request.post<Result<LeavesReportView[]>>(`reports/Leaves/`,
    serialize(input));
  return response.data;
}

export async function exportLeavesPerUser(input: LeavesReportInput, format: string) {
  const response = await request.post<ArrayBuffer>(`reports/Leaves/${format}`,
    serialize(input),
    {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json'
      }
    });
  const suggested = extractFilenameFromHeaders(response.headers);
  const fileName = suggested ?? buildFallbackFilename('leaves', format, input);
  const mime = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  downloadBlob(response.data, mime, fileName);
}

export async function getShiftsPerUser(input: ShiftsReportInput) {
  const response = await request.post<Result<ShiftReportView[]>>(`reports/Shifts/`,
    serialize(input));
  return response.data;
}

// HR analytics of out-of-bounds clocks — one row per breached clock side. Mirrors getShiftsPerUser
// (same [FromForm] serialize contract); returns a flat list (no server-side export endpoint).
export async function getOutOfBoundsClocks(input: OutOfBoundsReportInput) {
  const response = await request.post<Result<OutOfBoundsClockView[]>>(`reports/OutOfBounds/`,
    serialize(input));
  return response.data;
}

export async function exportShiftsPerUser(input: ShiftsReportInput, format: string) {
  const response = await request.post<ArrayBuffer>(`reports/Shifts/${format}`,
    serialize(input),
    {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json'
      }
    });
  const suggested = extractFilenameFromHeaders(response.headers);
  const fileName = suggested ?? buildFallbackFilename('shifts', format, input);
  const mime = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  downloadBlob(response.data, mime, fileName);
}

/**
 * The Leave Balance report's SCREEN call — a point-in-time snapshot, so the input carries no period.
 *
 * Same `[FromForm]` serialize contract as `getLeavesPerUser` above. `userIds` is not optional in
 * practice: `GetLeavesBalancePerUserAsync` iterates the list it is handed, so an empty one is an
 * empty report — the caller names the roster (the screen action does back-fill it server-side, but
 * naming it keeps the screen request and the export request identical).
 */
export async function getLeavesBalancePerUser(input: LeavesBalanceReportInput) {
  const response = await request.post<Result<LeavesBalanceReportView[]>>(`reports/LeavesBalance/`,
    serialize(input));
  return response.data;
}

export async function exportLeavesBalance(input: LeavesBalanceReportInput, format: string) {
  const response = await request.post<ArrayBuffer>(`reports/LeavesBalance/${format}`,
    serialize(input),
    {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json'
      }
    });
  const suggested = extractFilenameFromHeaders(response.headers);
  const fileName = suggested ?? buildFallbackFilename('leaves-balance', format, input);
  const mime = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  downloadBlob(response.data, mime, fileName);
}

export async function getUsersStatuses(input: UsersStatusesReportInput) {
  const response = await request.post<Result<UsersStatusesPaginatedView>>(`reports/UsersStatuses/`,
    serialize(input));
  return response.data;
}

export async function exportUsersStatuses(input: UsersStatusesReportInput, format: string) {
  const response = await request.post<ArrayBuffer>(`reports/UsersStatuses/${format}`,
    serialize(input),
    {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );
  const suggested = extractFilenameFromHeaders(response.headers);
  const fileName = suggested ?? buildFallbackFilename('users-statuses', format, input);
  const mime = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  downloadBlob(response.data, mime, fileName);
}

// CO-299 — the overtime numbers on screen. Same endpoint, same [FromForm] contract and the same
// input the export below posts, so /attendance/reports/overtime and the downloaded file can never
// disagree. Leave input.userIds empty to get every user the caller is authorised to see.
export async function getOvertimePerUser(input: OvertimeReportInput) {
  const response = await request.post<Result<OvertimeReportView[]>>(`reports/Overtime/`,
    serialize(input));
  return response.data;
}

export async function exportOvertimePerUser(input: OvertimeReportInput, format: string) {
  const response = await request.post<ArrayBuffer>(`reports/Overtime/${format}`,
    serialize(input),
    {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json'
      }
    });
  const suggested = extractFilenameFromHeaders(response.headers);
  const fileName = suggested ?? buildFallbackFilename('overtime', format, input);
  const mime = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  downloadBlob(response.data, mime, fileName);
}

export async function getWorkSessionsPerUser(input: WorkSessionsReportInput) {
  const response = await request.post<Result<WorkSessionReportView[]>>(`reports/WorkSessions/`,
    serialize(input));
  return response.data;
}

export async function exportWorkSessionsPerUser(input: WorkSessionsReportInput, format: string) {
  const response = await request.post<ArrayBuffer>(`reports/WorkSessions/${format}`,
    serialize(input),
    { responseType: 'blob', headers: { 'Content-Type': 'application/json' } });
  const suggested = extractFilenameFromHeaders(response.headers);
  const fileName = suggested ?? buildFallbackFilename('work-sessions', format, input);
  const mime = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  downloadBlob(response.data, mime, fileName);
}